/*
 * Chettyz product page (2026 layout).
 * Only touches elements inside [data-ctz-pdp] and the reels section.
 * Checkout, cart and Meta CAPI stay with checkout.chettyz.com: this file
 * never posts to /cart/add except in the Buy Now fallback, which runs only
 * when that script is missing (it stops the click before it reaches us).
 */
(() => {
  'use strict';

  const root = document.querySelector('[data-ctz-pdp]');
  const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- money ---------- */
  let moneyFormat = 'Rs. {{amount}}';
  try {
    const node = document.querySelector('[data-ctz-money-format]');
    if (node) moneyFormat = JSON.parse(node.textContent) || moneyFormat;
  } catch (e) {}

  const formatMoney = (cents) => {
    const value = Number(cents || 0) / 100;
    const withDecimals = value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const noDecimals = Math.round(value).toLocaleString('en-IN');
    return moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      if (key === 'amount_no_decimals' || key === 'amount_no_decimals_with_comma_separator') return noDecimals;
      if (key === 'amount_with_comma_separator') return withDecimals.replace('.', ',');
      return withDecimals;
    });
  };
  // Whole-rupee amount in the store's own format (e.g. "Rs. 428"), used for per-litre prices.
  const rupees = (cents) => {
    const whole = Math.round(Number(cents || 0) / 100).toLocaleString('en-IN');
    return moneyFormat.replace(/\{\{\s*\w+\s*\}\}/, whole);
  };

  /* ---------- unit price from variant title (500 ML, 1 LTR, 250 g, 1 kg) ---------- */
  const unitFor = (title) => {
    const m = String(title || '').match(/(\d+(?:\.\d+)?)\s*(ml|ltr|litres?|liters?|l|kgs?|g|gms?|grams?)\b/i);
    if (!m) return null;
    const qty = parseFloat(m[1]);
    const u = m[2].toLowerCase();
    if (!qty) return null;
    if (u === 'ml') return { base: qty / 1000, label: 'litre' };
    if (u.startsWith('l')) return { base: qty, label: 'litre' };
    if (u.startsWith('kg')) return { base: qty, label: 'kg' };
    return { base: qty / 1000, label: 'kg' };
  };
  // Price per litre/kg only where it helps (oils and foods, not soaps, towels or combos).
  const showUnit = !root || root.getAttribute('data-unit-price') !== '0';
  const unitText = (variant) => {
    if (!showUnit) return '';
    const unit = unitFor(variant && variant.title);
    if (!unit) return '';
    return rupees(variant.price / unit.base) + ' per ' + unit.label;
  };

  /* ---------- countdown (also used outside the product page markup) ---------- */
  const initCountdown = () => {
    const box = root && root.querySelector('[data-ctz-countdown]');
    if (!box) return;
    const endRaw = root.getAttribute('data-diwali-end') || '';
    const end = new Date(endRaw + 'T23:59:59+05:30').getTime();
    if (!end || Number.isNaN(end)) return;
    const d = box.querySelector('[data-cd-d]');
    const h = box.querySelector('[data-cd-h]');
    const m = box.querySelector('[data-cd-m]');
    const tick = () => {
      const left = end - Date.now();
      if (left <= 0) { box.hidden = true; return; }
      box.hidden = false;
      d.textContent = Math.floor(left / 86400000);
      h.textContent = Math.floor((left % 86400000) / 3600000);
      m.textContent = String(Math.floor((left % 3600000) / 60000)).padStart(2, '0');
    };
    tick();
    setInterval(tick, 30000);
  };

  /* ---------- gallery ---------- */
  const initGallery = () => {
    const gallery = root && root.querySelector('[data-ctz-gallery]');
    if (!gallery) return;
    const track = gallery.querySelector('[data-ctz-track]');
    const slides = Array.from(gallery.querySelectorAll('[data-ctz-slide]'));
    const thumbs = Array.from(gallery.querySelectorAll('[data-ctz-thumb]'));
    const dots = Array.from(gallery.querySelectorAll('[data-ctz-dot]'));
    if (!track || slides.length < 2) return;

    const setActive = (index) => {
      thumbs.forEach((t, i) => {
        t.classList.toggle('is-active', i === index);
        if (i === index) t.setAttribute('aria-current', 'true'); else t.removeAttribute('aria-current');
      });
      dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
      slides.forEach((slide, i) => {
        const video = slide.querySelector('video');
        if (!video) return;
        if (i === index) { video.muted = true; const p = video.play(); if (p && p.catch) p.catch(() => {}); }
        else video.pause();
      });
    };

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const index = Number(thumb.getAttribute('data-ctz-thumb'));
        track.scrollTo({ left: slides[index].offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
        setActive(index);
      });
    });

    let raf = 0;
    track.addEventListener('scroll', () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const index = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
        setActive(Math.min(slides.length - 1, Math.max(0, index)));
      });
    }, { passive: true });
  };

  /* ---------- variants, price, coins, shipping ---------- */
  const initVariants = () => {
    if (!root) return;
    let product = null;
    try { product = JSON.parse(root.querySelector('[data-product-json]').textContent); } catch (e) { return; }
    const form = document.getElementById(root.getAttribute('data-form-id'));
    if (!form) return;
    const idInput = form.querySelector('[name="id"]');
    const shipMin = Number(root.getAttribute('data-ship-min') || 49900);

    const el = (sel) => root.querySelector(sel);
    const all = (sel) => Array.from(root.querySelectorAll(sel));
    const addBtn = form.querySelector('button[name="add"]');
    const addLabel = addBtn && addBtn.querySelector('.btn-label');
    const addLabelDefault = addLabel ? addLabel.textContent.trim() : '';
    const stickyBtn = root.querySelector('.ctz-pdp-sticky__btn');

    const currentVariant = () => {
      const id = Number(idInput && idInput.value);
      return product.variants.find((v) => v.id === id) || product.variants[0];
    };
    const quantity = () => {
      const q = form.querySelector('[name="quantity"]') || document.querySelector('[name="quantity"][form="' + form.id + '"]');
      return Math.max(1, Number(q && q.value) || 1);
    };

    // Per-pack unit prices
    product.variants.forEach((v) => {
      const target = root.querySelector('[data-ctz-pack-unit="' + v.id + '"]');
      const text = unitText(v);
      if (target && text) target.textContent = text;
    });

    const render = () => {
      const v = currentVariant();
      const qty = quantity();
      const compare = Number(v.compare_at_price || 0);
      const onSale = compare > v.price;
      const pct = onSale ? Math.round(((compare - v.price) * 100) / compare) : 0;

      const price = el('[data-ctz-price]');
      if (price) price.textContent = formatMoney(v.price);
      const was = el('[data-ctz-compare]');
      if (was) { was.textContent = formatMoney(compare); was.hidden = !onSale; }
      all('[data-ctz-savepct]').forEach((n) => { n.textContent = pct + '%'; });
      const pill = el('[data-ctz-savepill]');
      if (pill) pill.hidden = !onSale;
      const badge = el('[data-ctz-savebadge]');
      if (badge) badge.hidden = !onSale;
      const saveLine = el('[data-ctz-saveline]');
      if (saveLine) saveLine.hidden = !onSale;
      const saveAmt = el('[data-ctz-saveamt]');
      if (saveAmt) saveAmt.textContent = formatMoney(compare - v.price);
      const unit = el('[data-ctz-unit]');
      if (unit) { const t = unitText(v); unit.textContent = t ? t + ' · ' : ''; }

      const coins = el('[data-ctz-coins]');
      if (coins) coins.textContent = Math.floor((v.price * qty) / 100).toLocaleString('en-IN');

      const total = v.price * qty;
      const note = el('[data-ctz-ship-note]');
      const bar = el('[data-ctz-ship-bar]');
      if (note) note.textContent = total >= shipMin ? 'Your order qualifies for free shipping.' : 'Add ' + formatMoney(shipMin - total) + ' more for free shipping.';
      if (bar) bar.style.width = Math.min(100, Math.round((total / shipMin) * 100)) + '%';

      const sTitle = el('[data-ctz-sticky-title]');
      if (sTitle && product.variants.length > 1) sTitle.textContent = v.title;
      const sPrice = el('[data-ctz-sticky-price]');
      if (sPrice) sPrice.textContent = formatMoney(v.price);

      if (addBtn) addBtn.disabled = !v.available;
      if (addLabel) addLabel.textContent = v.available ? addLabelDefault : 'Sold out';
      if (stickyBtn) stickyBtn.disabled = !v.available;
      const buyNow = form.querySelector('[data-ctz-buy-now]');
      if (buyNow) buyNow.disabled = !v.available;

      all('.ctz-pdp-pack').forEach((card) => {
        const input = card.querySelector('[data-ctz-pack]');
        card.classList.toggle('is-selected', !!input && Number(input.value) === v.id);
      });
    };

    root.addEventListener('change', (event) => {
      const input = event.target.closest && event.target.closest('[data-ctz-pack]');
      if (!input || !idInput) return;
      idInput.value = input.value;
      idInput.dispatchEvent(new Event('change', { bubbles: true }));
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', input.value);
        window.history.replaceState({}, '', url.toString());
      } catch (e) {}
      render();
    });

    // Quantity changes (theme quantity-input dispatches change on the input)
    document.addEventListener('change', (event) => {
      if (event.target && event.target.name === 'quantity') render();
    });
    document.addEventListener('click', (event) => {
      if (event.target.closest && event.target.closest('quantity-input')) setTimeout(render, 30);
    });

    render();
  };

  /* ---------- story circles -> popup ---------- */
  const initStories = () => {
    if (!root) return;
    const modal = root.querySelector('[data-ctz-modal]');
    const slot = modal && modal.querySelector('[data-ctz-modal-slot]');
    if (!modal || !slot) return;
    let lastTrigger = null;

    const close = () => {
      slot.querySelectorAll('video').forEach((v) => v.pause());
      slot.innerHTML = '';
      modal.hidden = true;
      document.documentElement.classList.remove('ctz-pdp-lock');
      if (lastTrigger) lastTrigger.focus();
    };

    root.querySelectorAll('[data-ctz-story]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const tpl = btn.querySelector('[data-ctz-story-template]');
        if (!tpl) return;
        lastTrigger = btn;
        slot.innerHTML = '';
        slot.appendChild(tpl.content.cloneNode(true));
        modal.hidden = false;
        document.documentElement.classList.add('ctz-pdp-lock');
        const video = slot.querySelector('video');
        if (video) {
          video.setAttribute('playsinline', '');
          const p = video.play();
          if (p && p.catch) p.catch(() => { video.muted = true; video.play().catch(() => {}); });
        }
        const closeBtn = modal.querySelector('.ctz-pdp-modal__close');
        if (closeBtn) closeBtn.focus();
      });
    });

    // Live preview inside each circle: muted, looping, only while on screen.
    const live = Array.from(root.querySelectorAll('[data-ctz-story-live] video'));
    live.forEach((v) => {
      v.muted = true; v.loop = true; v.setAttribute('playsinline', ''); v.removeAttribute('controls');
      v.setAttribute('aria-hidden', 'true'); v.tabIndex = -1;
    });
    if (live.length && !reduceMotion) {
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            const v = entry.target;
            if (entry.isIntersecting) { const p = v.play(); if (p && p.catch) p.catch(() => {}); }
            else v.pause();
          });
        }, { threshold: 0.25 });
        live.forEach((v) => io.observe(v));
      } else {
        live.forEach((v) => { const p = v.play(); if (p && p.catch) p.catch(() => {}); });
      }
    } else {
      live.forEach((v) => v.pause());
    }

    modal.querySelectorAll('[data-ctz-modal-close]').forEach((b) => b.addEventListener('click', close));
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !modal.hidden) close(); });
  };

  /* ---------- reels: play while on screen ---------- */
  const initReels = () => {
    const frames = Array.from(document.querySelectorAll('[data-ctz-reel]'));
    if (!frames.length) return;
    const videos = frames.map((f) => f.querySelector('video')).filter(Boolean);
    videos.forEach((v) => { v.muted = true; v.loop = true; v.setAttribute('playsinline', ''); v.removeAttribute('controls'); });

    if ('IntersectionObserver' in window && !reduceMotion) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const v = entry.target;
          if (entry.isIntersecting) { const p = v.play(); if (p && p.catch) p.catch(() => {}); }
          else v.pause();
        });
      }, { threshold: 0.5 });
      videos.forEach((v) => io.observe(v));
    }

    frames.forEach((frame) => {
      const btn = frame.querySelector('[data-ctz-reel-sound]');
      const video = frame.querySelector('video');
      if (!btn || !video) return;
      btn.addEventListener('click', () => {
        const turnOn = video.muted;
        videos.forEach((v) => { v.muted = true; });
        frames.forEach((f) => f.classList.remove('is-sound'));
        if (turnOn) {
          video.muted = false;
          frame.classList.add('is-sound');
          const p = video.play(); if (p && p.catch) p.catch(() => {});
        }
        btn.setAttribute('aria-label', turnOn ? 'Mute video' : 'Turn on sound');
      });
    });
  };

  /* ---------- mobile sticky bar ---------- */
  const initSticky = () => {
    if (!root) return;
    const bar = root.querySelector('[data-ctz-sticky]');
    const cta = root.querySelector('[data-ctz-cta]');
    if (!bar || !cta || !('IntersectionObserver' in window)) return;
    const btn = bar.querySelector('button');
    const io = new IntersectionObserver(([entry]) => {
      const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
      if (btn) btn.tabIndex = show ? 0 : -1;
    });
    io.observe(cta);
  };

  /* ---------- Buy Now fallback (only if checkout.chettyz.com did not handle it) ---------- */
  const initBuyNowFallback = () => {
    if (!root) return;
    root.addEventListener('click', async (event) => {
      const btn = event.target.closest && event.target.closest('[data-ctz-buy-now]');
      if (!btn || event.defaultPrevented) return;
      const form = btn.closest('form') || document.getElementById(root.getAttribute('data-form-id'));
      const id = form && form.querySelector('[name="id"]');
      const qty = form && form.querySelector('[name="quantity"]');
      if (!id || !id.value) return;
      btn.disabled = true;
      try {
        const res = await fetch(btn.getAttribute('data-cart-add-url') || '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(id.value), quantity: Math.max(1, Number(qty && qty.value) || 1) }] })
        });
        if (!res.ok) throw new Error('add failed');
        window.location.href = btn.getAttribute('data-checkout-url') || '/checkout';
      } catch (e) {
        btn.disabled = false;
      }
    });
  };

  /* ---------- "Read customer reviews" scrolls to the reviews app ---------- */
  const initReviewsLink = () => {
    if (!root) return;
    root.addEventListener('click', (event) => {
      const trigger = event.target.closest && event.target.closest('[data-ctz-scroll-reviews]');
      if (!trigger) return;
      const target =
        document.querySelector('[id*="air_product_reviews"]') ||
        document.querySelector('.airReviews') ||
        document.querySelector('[class*="air-review"]') ||
        document.querySelector('#judgeme_product_reviews') ||
        document.querySelector('.jdgm-rev-widg') ||
        document.querySelector('.spr-container') ||
        document.querySelector('[id*="review"][class*="shopify-section"]');
      if (target) target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    });
  };

  const start = () => {
    initReviewsLink();
    initCountdown();
    initGallery();
    initVariants();
    initStories();
    initReels();
    initSticky();
    initBuyNowFallback();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
